import {
  appendTxnBenchmarkColumns,
  orderTxnBenchmarkAndMemberColumns,
} from "./transactionBenchmarkColumns";

export type TxnMetadataColumn = {
  key: string;
  value: string;
  minWidth?: number;
  locked?: boolean;
};

/** Transaction cost fields — inserted just before Member Name (all reports). */
export const TXN_COST_COLUMNS_BEFORE_MEMBER: TxnMetadataColumn[] = [
  { key: "stt", value: "STT", minWidth: 88 },
  { key: "stampDuty", value: "Stamp Duty", minWidth: 100 },
  { key: "otherExpenses", value: "Other Expenses", minWidth: 112 },
  { key: "brokerage", value: "Brokerage", minWidth: 96 },
];

export const TXN_COST_COLUMN_KEYS = TXN_COST_COLUMNS_BEFORE_MEMBER.map(
  (c) => c.key,
);

export const TXN_COST_COLUMN_KEY_SET = new Set<string>(TXN_COST_COLUMN_KEYS);

/** Common transaction-level metaData fields (non-cost) — inserted before P&L. */
export const TXN_METADATA_COMMON_COLUMNS: TxnMetadataColumn[] = [
  { key: "dpName", value: "DP Name", minWidth: 96 },
];

/** Holding-level dividend-only cols before P&L (cost cols use TXN_COST_COLUMNS_BEFORE_MEMBER). */
export const HOLDING_LEVEL_COST_COLUMNS: TxnMetadataColumn[] = [];

export const HOLDING_LEVEL_DP_NAME_COLUMN: TxnMetadataColumn = {
  key: "dpName",
  value: "DP Name",
  minWidth: 96,
};

export const TXN_METADATA_DIVIDEND_COLUMN: TxnMetadataColumn = {
  key: "dividend",
  value: "Dividend",
  minWidth: 96,
};

/** REIT distribution fields from getHoldings metaData (after Dividend). */
export const TXN_METADATA_REIT_DISTRIBUTION_COLUMNS: TxnMetadataColumn[] = [
  { key: "interest", value: "Interest", minWidth: 96 },
  { key: "capitalRepayment", value: "Capital Repayment", minWidth: 132 },
  { key: "otherComponents", value: "Other Components", minWidth: 128 },
];

/** AIF-only transaction metaData fields. */
export const TXN_METADATA_AIF_COLUMNS: TxnMetadataColumn[] = [
  { key: "netEarned", value: "Net Earned", minWidth: 112 },
  { key: "grossContribution", value: "Gross Contribution", minWidth: 132 },
  { key: "contributedCapital", value: "Contributed Capital", minWidth: 132 },
  { key: "balanceCapital", value: "Balance Capital", minWidth: 120 },
  { key: "principalRepaid", value: "Principal Repaid", minWidth: 120 },
  { key: "unitsAlloted", value: "Units Allotted", minWidth: 108 },
  { key: "unitsAvailable", value: "Units Available", minWidth: 112 },
  { key: "unitsFree", value: "Units Free", minWidth: 96 },
  { key: "unitsPledged", value: "Units Pledged", minWidth: 108 },
];

export const TXN_METADATA_DIVIDEND_SEGMENTS = new Set([
  "STOCK",
  "REIT",
  "PRIVATE_STOCK",
]);

export const TXN_METADATA_COLUMN_KEYS = [
  ...TXN_COST_COLUMN_KEYS,
  ...TXN_METADATA_COMMON_COLUMNS.map((c) => c.key),
  TXN_METADATA_DIVIDEND_COLUMN.key,
  ...TXN_METADATA_REIT_DISTRIBUTION_COLUMNS.map((c) => c.key),
  ...TXN_METADATA_AIF_COLUMNS.map((c) => c.key),
] as const;

const TXN_METADATA_COLUMN_KEY_SET = new Set<string>(TXN_METADATA_COLUMN_KEYS);

/** P&L column keys — metadata cols are inserted immediately before the first match. */
const TXN_PNL_COLUMN_KEYS = new Set([
  "gainLoss",
  "gainLossPerc",
  "gainLossPercentage",
  "unrealisedPNL",
  "unrealisedPnL",
  "unrealizedPNL",
  "unrealizedPnl",
  "unrealisedPNLPercentage",
  "unrealisedPnLPercentage",
  "unrealizedPNLPercentage",
  "unrealizedPnLPercentage",
  "unrealisedGainLoss",
  "unrealizedGainLoss",
  "unrealisedGainLossPercentage",
  "unrealizedGainLossPercentage",
  "realisedPnl",
  "realisedPNL",
  "realizedPnl",
  "realizedPNL",
  "realisedPNLPercentage",
  "realisedPnLPercentage",
  "realizedPNLPercentage",
  "realizedPnLPercentage",
  "unrealisedPNLCombined",
  "realisedPNLCombined",
  "cuurentPnL",
  "currentPnLinPercentage",
]);

const isTxnPnlColumn = (col: { key: string; value?: string }): boolean => {
  const key = col.key.trim();
  if (TXN_PNL_COLUMN_KEYS.has(key)) return true;
  const label = String(col.value ?? "").toLowerCase();
  return label.includes("p&l") || label.includes("pnl");
};

export const TXN_METADATA_CURRENCY_KEYS = new Set([
  "stt",
  "brokerage",
  "stampDuty",
  "otherExpenses",
  "dividend",
  "interest",
  "capitalRepayment",
  "otherComponents",
  "netEarned",
  "grossContribution",
  "contributedCapital",
  "balanceCapital",
  "principalRepaid",
]);

/** Show ₹0.00 instead of "-" when API sends 0 (REIT distribution fields). */
export const TXN_METADATA_SHOW_ZERO_CURRENCY_KEYS = new Set([
  "dividend",
  "interest",
  "capitalRepayment",
  "otherComponents",
]);

export const TXN_METADATA_UNITS_KEYS = new Set([
  "unitsAlloted",
  "unitsAvailable",
  "unitsFree",
  "unitsPledged",
]);

export const TXN_METADATA_TEXT_KEYS = new Set(["dpName"]);

const readTxnMeta = (row: Record<string, unknown>): Record<string, unknown> => {
  const raw =
    row.metadata ?? row.metaData ?? row.meta ?? row.MetaData ?? {};
  return raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
};

const pickMetaValue = (
  meta: Record<string, unknown>,
  row: Record<string, unknown>,
  ...keys: string[]
): unknown => {
  for (const key of keys) {
    const fromMeta = meta[key];
    if (fromMeta !== null && fromMeta !== undefined && fromMeta !== "") {
      return fromMeta;
    }
    const fromRow = row[key];
    if (fromRow !== null && fromRow !== undefined && fromRow !== "") {
      return fromRow;
    }
  }
  return null;
};

/** DP Name — plain text from backend; never currency/number formatted. */
const pickDpName = (
  meta: Record<string, unknown>,
  row: Record<string, unknown>,
): string => {
  for (const raw of [meta.dpName, row.dpName]) {
    if (raw === null || raw === undefined) continue;
    if (typeof raw === "string") return raw;
    return String(raw);
  }
  return "";
};

/**
 * Prefer row/meta field; for holdings, fall back to summing nested txn metaData
 * (REIT dividend / interest / capitalRepayment / otherComponents live on txns).
 */
const resolveDistributionField = (
  meta: Record<string, unknown>,
  row: Record<string, unknown>,
  field: string,
): unknown => {
  const direct = pickMetaValue(meta, row, field);
  if (direct !== null) return direct;

  const txns = row.transactions;
  if (!Array.isArray(txns) || txns.length === 0) return null;

  let sum = 0;
  let found = false;
  for (const txn of txns) {
    if (!txn || typeof txn !== "object") continue;
    const t = txn as Record<string, unknown>;
    const tMeta = readTxnMeta(t);
    const v = pickMetaValue(tMeta, t, field);
    if (v === null) continue;
    found = true;
    const n = Number(v);
    if (Number.isFinite(n)) sum += n;
  }
  return found ? sum : null;
};

export const extractTxnMetadataFields = (
  row: Record<string, unknown>,
): Record<string, unknown> => {
  const meta = readTxnMeta(row);

  return {
    stt: pickMetaValue(meta, row, "stt"),
    dpName: pickDpName(meta, row),
    brokerage: pickMetaValue(meta, row, "brokerage"),
    stampDuty: pickMetaValue(meta, row, "stampDuty"),
    otherExpenses: pickMetaValue(meta, row, "otherExpenses"),
    dividend: resolveDistributionField(meta, row, "dividend"),
    interest: resolveDistributionField(meta, row, "interest"),
    capitalRepayment: resolveDistributionField(meta, row, "capitalRepayment"),
    otherComponents: resolveDistributionField(meta, row, "otherComponents"),
    netEarned: pickMetaValue(meta, row, "netEarned"),
    unitsFree: pickMetaValue(meta, row, "unitsFree"),
    unitsAlloted: pickMetaValue(meta, row, "unitsAlloted", "unitsAllotted"),
    unitsPledged: pickMetaValue(meta, row, "unitsPledged"),
    unitsAvailable: pickMetaValue(meta, row, "unitsAvailable"),
    principalRepaid: pickMetaValue(meta, row, "principalRepaid"),
    grossContribution: pickMetaValue(meta, row, "grossContribution"),
    contributedCapital: pickMetaValue(meta, row, "contributedCapital"),
    balanceCapital: pickMetaValue(meta, row, "balanceCapital"),
  };
};

export const withTxnMetadataFields = <T extends Record<string, unknown>>(
  row: T,
): T & ReturnType<typeof extractTxnMetadataFields> => ({
  ...row,
  ...extractTxnMetadataFields(row),
});

export const getTxnMetadataColumnsForSegment = (
  segment: string,
): TxnMetadataColumn[] => {
  const columns: TxnMetadataColumn[] = [...TXN_METADATA_COMMON_COLUMNS];
  if (TXN_METADATA_DIVIDEND_SEGMENTS.has(segment)) {
    columns.push(TXN_METADATA_DIVIDEND_COLUMN);
  }
  if (segment === "REIT") {
    columns.push(...TXN_METADATA_REIT_DISTRIBUTION_COLUMNS);
  }
  if (segment === "AIF") {
    columns.push(...TXN_METADATA_AIF_COLUMNS);
  }
  return columns;
};

export const appendTxnMetadataColumns = <T extends TxnMetadataColumn>(
  columns: T[],
  segment: string,
): T[] => {
  const metaCols = getTxnMetadataColumnsForSegment(segment) as T[];
  return insertMetadataColumnsBeforePnl(columns, metaCols);
};

export const getHoldingLevelCostColumnsForSegment = (
  segment: string,
): TxnMetadataColumn[] => {
  const columns: TxnMetadataColumn[] = [...HOLDING_LEVEL_COST_COLUMNS];
  if (TXN_METADATA_DIVIDEND_SEGMENTS.has(segment)) {
    columns.push(TXN_METADATA_DIVIDEND_COLUMN);
  }
  if (segment === "REIT") {
    columns.push(...TXN_METADATA_REIT_DISTRIBUTION_COLUMNS);
  }
  return columns;
};

export const getHoldingLevelCostColumnsWithDpName = (
  segment?: string,
): TxnMetadataColumn[] => [
  ...getHoldingLevelCostColumnsForSegment(segment ?? ""),
  HOLDING_LEVEL_DP_NAME_COLUMN,
];

/** Cost cols are positioned before Member Name via insertTxnCostColumnsBeforeMember. */
export const appendHoldingLevelCostColumns = <T extends TxnMetadataColumn>(
  columns: T[],
  segment?: string,
): T[] =>
  insertMetadataColumnsBeforePnl(
    columns,
    getHoldingLevelCostColumnsForSegment(segment ?? "") as T[],
  );

export const appendHoldingLevelCostColumnsWithDpName = <
  T extends TxnMetadataColumn,
>(
  columns: T[],
  segment?: string,
): T[] =>
  insertMetadataColumnsBeforePnl(
    columns,
    [HOLDING_LEVEL_DP_NAME_COLUMN] as T[],
  );

function insertMetadataColumnsBeforePnl<T extends TxnMetadataColumn>(
  columns: T[],
  metaCols: T[],
): T[] {
  if (metaCols.length === 0) return columns;

  const metaKeySet = new Set(metaCols.map((c) => c.key));
  const withoutMeta = columns.filter(
    (c) => !metaKeySet.has(c.key) && !TXN_COST_COLUMN_KEY_SET.has(c.key),
  );
  const firstPnlIdx = withoutMeta.findIndex((c) => isTxnPnlColumn(c));
  const insertAt = firstPnlIdx >= 0 ? firstPnlIdx : withoutMeta.length;

  return [
    ...withoutMeta.slice(0, insertAt),
    ...metaCols,
    ...withoutMeta.slice(insertAt),
  ];
}

/** STT / Stamp Duty / Other Expenses / Brokerage — immediately before Member Name. */
export const insertTxnCostColumnsBeforeMember = <T extends TxnMetadataColumn>(
  columns: T[],
): T[] => {
  if (columns.length === 0) return columns;

  const withoutCost = columns.filter((c) => !TXN_COST_COLUMN_KEY_SET.has(c.key));
  const costCols = TXN_COST_COLUMNS_BEFORE_MEMBER.map((def) => {
    const existing = columns.find((c) => c.key === def.key);
    return (existing ?? def) as T;
  });

  const memberIdx = withoutCost.findIndex((c) => c.key === "memberName");
  if (memberIdx >= 0) {
    return [
      ...withoutCost.slice(0, memberIdx),
      ...costCols,
      ...withoutCost.slice(memberIdx),
    ];
  }

  return [...withoutCost, ...costCols];
};

/** Benchmark + member ordering, then cost cols immediately before Member Name. */
export const orderTransactionCostBenchmarkAndMemberColumns = <
  T extends TxnMetadataColumn,
>(
  columns: T[],
  options?: { includeMemberName?: boolean; memberColumn?: T },
): T[] =>
  insertTxnCostColumnsBeforeMember(
    orderTxnBenchmarkAndMemberColumns(columns, options),
  );

/** Metadata before P&L columns; Nifty/BSE benchmarks appended last (when enabled). */
export const withTxnMetadataAndBenchmarkColumns = <T extends TxnMetadataColumn>(
  columns: T[],
  segment: string,
  options?: { includeBenchmarks?: boolean },
): T[] => {
  const includeBenchmarks = options?.includeBenchmarks !== false;
  const withMeta = appendTxnMetadataColumns(columns, segment);
  if (!includeBenchmarks) return withMeta;
  return appendTxnBenchmarkColumns(withMeta);
};

export const isTxnMetadataColumn = (key: string): boolean =>
  TXN_METADATA_COLUMN_KEY_SET.has(key);

/** Text metadata (e.g. DP Name) — show backend value as-is, no numeric formatting. */
export const formatTxnMetadataText = (value: unknown): string => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "string") {
    return value.trim() === "" ? "-" : value;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "-";
    return String(value);
  }
  const str = String(value);
  return str.trim() === "" ? "-" : str;
};

export const formatTxnMetadataCurrency = (
  value: unknown,
  options?: { showZero?: boolean },
): string => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  if (num === 0 && !options?.showZero) return "-";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatTxnMetadataUnits = (value: unknown): string => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
};
